'use client';

import { useSyncExternalStore } from 'react';
import type { FaceMeasurementRecord, PdMeasurementRecord } from './types';

let currentFaceWidthRecord: FaceMeasurementRecord | null = null;
let currentPdRecord: PdMeasurementRecord | null = null;
let currentFeedbackSubmitted = false;
let currentMeasurementSessionReady = false;
let currentMeasurementSessionAuthoritative = false;
let unconsumedPdRecordId: string | null = null;
const faceWidthListeners = new Set<() => void>();
const pdListeners = new Set<() => void>();
const feedbackListeners = new Set<() => void>();
const readinessListeners = new Set<() => void>();

function subscribeFaceWidth(listener: () => void) {
  faceWidthListeners.add(listener);
  return () => faceWidthListeners.delete(listener);
}

function subscribePd(listener: () => void) {
  pdListeners.add(listener);
  return () => pdListeners.delete(listener);
}

function subscribeFeedback(listener: () => void) {
  feedbackListeners.add(listener);
  return () => feedbackListeners.delete(listener);
}

function subscribeReadiness(listener: () => void) {
  readinessListeners.add(listener);
  return () => readinessListeners.delete(listener);
}

function getFaceWidthSnapshot() {
  return currentFaceWidthRecord;
}

function getPdSnapshot() {
  return currentPdRecord;
}

function getFeedbackSnapshot() {
  return currentFeedbackSubmitted;
}

function getReadinessSnapshot() {
  return currentMeasurementSessionReady;
}

function getAuthoritativeSnapshot() {
  return currentMeasurementSessionAuthoritative;
}

function getServerFeedbackSnapshot() {
  return false;
}

function getServerSnapshot() {
  return null;
}

export function saveFaceMeasurementSession(record: FaceMeasurementRecord) {
  currentFaceWidthRecord = record;
  faceWidthListeners.forEach((listener) => listener());
}

export function savePdMeasurementSession(record: PdMeasurementRecord) {
  currentPdRecord = record;
  unconsumedPdRecordId = record.recordId;
  pdListeners.forEach((listener) => listener());
}

export function replaceMeasurementSession({
  faceWidth,
  pd,
  feedbackSubmitted,
}: {
  faceWidth: FaceMeasurementRecord | null;
  pd: PdMeasurementRecord | null;
  feedbackSubmitted: boolean;
}) {
  currentFaceWidthRecord = faceWidth;
  currentPdRecord = pd;
  currentFeedbackSubmitted = feedbackSubmitted;
  unconsumedPdRecordId = null;
  faceWidthListeners.forEach((listener) => listener());
  pdListeners.forEach((listener) => listener());
  feedbackListeners.forEach((listener) => listener());
}

export function setMeasurementFeedbackSubmitted(submitted: boolean) {
  if (currentFeedbackSubmitted === submitted) return;
  currentFeedbackSubmitted = submitted;
  feedbackListeners.forEach((listener) => listener());
}

export function setMeasurementSessionReady(ready: boolean) {
  const authoritative = ready;
  if (
    currentMeasurementSessionReady === ready &&
    currentMeasurementSessionAuthoritative === authoritative
  ) {
    return;
  }
  currentMeasurementSessionReady = ready;
  currentMeasurementSessionAuthoritative = authoritative;
  readinessListeners.forEach((listener) => listener());
}

export function setMeasurementSessionUnavailable() {
  if (currentMeasurementSessionReady) return;
  currentMeasurementSessionReady = true;
  currentMeasurementSessionAuthoritative = false;
  readinessListeners.forEach((listener) => listener());
}

export function waitForMeasurementSessionReady() {
  if (currentMeasurementSessionReady) return Promise.resolve();

  return new Promise<void>((resolve) => {
    const listener = () => {
      if (!currentMeasurementSessionReady) return;
      readinessListeners.delete(listener);
      resolve();
    };
    readinessListeners.add(listener);
  });
}

export function getMeasurementFeedbackSubmitted() {
  return currentFeedbackSubmitted;
}

export function readFaceMeasurementSession() {
  return currentFaceWidthRecord;
}

export function isMeasurementSessionAuthoritative() {
  return currentMeasurementSessionAuthoritative;
}

export function readPdMeasurementSession() {
  return currentPdRecord;
}

export function consumePdMeasurementSession(recordId: string) {
  if (unconsumedPdRecordId !== recordId) return false;
  unconsumedPdRecordId = null;
  return true;
}

export function clearFaceMeasurementSession() {
  currentFaceWidthRecord = null;
  faceWidthListeners.forEach((listener) => listener());
}

export function clearPdMeasurementSession() {
  currentPdRecord = null;
  unconsumedPdRecordId = null;
  pdListeners.forEach((listener) => listener());
}

export function useFaceMeasurementSession() {
  return useSyncExternalStore(subscribeFaceWidth, getFaceWidthSnapshot, getServerSnapshot);
}

export function usePdMeasurementSession() {
  return useSyncExternalStore(subscribePd, getPdSnapshot, getServerSnapshot);
}

export function useMeasurementFeedbackSubmitted() {
  return useSyncExternalStore(subscribeFeedback, getFeedbackSnapshot, getServerFeedbackSnapshot);
}

export function useMeasurementSessionReady() {
  return useSyncExternalStore(subscribeReadiness, getReadinessSnapshot, getServerFeedbackSnapshot);
}

export function useMeasurementSessionAuthoritative() {
  return useSyncExternalStore(
    subscribeReadiness,
    getAuthoritativeSnapshot,
    getServerFeedbackSnapshot,
  );
}

export function useActiveFaceMeasurement() {
  return useFaceMeasurementSession();
}

export function useActivePdMeasurement() {
  return usePdMeasurementSession();
}
